import React from 'react';
import { useFeatureServices } from '../context/ofertas-context';

import {
	MaintenanceTrackingEvent,
	OfertasTrackingManager,
	TrackingEvent,
	TrackingEventInfo,
	TrackingLink,
	TrackingPersonalization,
} from './tracking-types';

const baseEventInfo: TrackingEventInfo = {
	brand: 'passenger',
	pageCategory: 'Ofertas',
	sectionName: undefined,
	templateType: 'Feature App Template',
};

const personalization: TrackingPersonalization = {
	status: false,
	placeholderId: undefined,
	contentId: undefined,
	activityId: undefined,
	activityName: undefined,
	experienceId: undefined,
	experienceName: undefined,
	offerId: undefined,
	offerName: undefined,
};

const defaultTrackingEvent: MaintenanceTrackingEvent = {
	personalization,
};

const createTrackingLink = (
	linkName: string | undefined,
): TrackingLink | undefined => {
	const link = {
		url: window ? window.location.href : undefined,
	};

	return { ...link, name: linkName };
};

export const useTrackingManager = (): OfertasTrackingManager => {
	const { tracking: trackingManager } = useFeatureServices();

	return React.useMemo((): OfertasTrackingManager => {
		function track(action: string, event: MaintenanceTrackingEvent): void {
			if (trackingManager) {
				trackingManager.track(action, event as TrackingEvent);
			}
		}

		return {
			trackOfertasGenericFeatureAppLoad(configurationInfo, eventInfo?) {
				track('VWBasic_GenericFeatureApp_Load', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
				});
			},

			trackOfertasFilterSelectClick(filterInfo, eventInfo?) {
				track('VWBasic_FilterSelect_Click', {
					...defaultTrackingEvent,
					filter: filterInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink('Filter : ' + filterInfo.values[0]),
				});
			},

			trackOfertasFilterSearchResultLoad(
				filterInfo,
				configurationInfo,
				eventInfo?,
			) {
				track('VWBasic_FilterSearchResult_Load', {
					...defaultTrackingEvent,
					filter: filterInfo,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
				});
			},

			trackOfertasModelSelectClick(configurationInfo, eventInfo?) {
				track('VWBasic_ModelSelect_Click', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink('Model : ' + configurationInfo.CarlineName),
				});
			},

			trackOfertasVehiclePageload(configurationInfo, eventInfo) {
				track('VWBasic_Vehicle_Pageload', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
				});
			},

			trackOfertasVehicleLoad(configurationInfo, eventInfo) {
				track('VWBasic_Vehicle_Load', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
				});
			},

			trackOfertasButtonClick(
				linkName,
				eventInfo?,
				configurationInfo?,
				contentInfo?,
			) {
				track('VWBasic_Button_Click', {
					...defaultTrackingEvent,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					configuration: configurationInfo,
					content: contentInfo ? { Name: contentInfo } : undefined,
				});
			},

			trackOfertasLinkClick(linkName, eventInfo?, configurationInfo?) {
				track('VWBasic_Link_Click', {
					...defaultTrackingEvent,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					configuration: configurationInfo,
				});
			},

			trackOfertasLayerLoad(eventInfo?, configurationInfo?, contentInfo?) {
				track('VWBasic_Layer_Load', {
					...defaultTrackingEvent,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					configuration: configurationInfo,
					content: { Name: contentInfo },
					link: createTrackingLink(eventInfo?.contentId),
				});
			},

			trackOfertasLayerClose(
				linkName,
				eventInfo?,
				configurationInfo?,
				contentInfo?,
			) {
				track('VWBasic_LayerClose_Click', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					content: { Name: contentInfo },
				});
			},

			trackOfertasElementSwipe(configurationInfo, eventInfo, contentInfo) {
				track('VWBasic_Element_Swipe', {
					...defaultTrackingEvent,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					content: { Name: contentInfo },
				});
			},
		};
	}, [trackingManager]);
};
